/**
 * Timezone utilities for displaying inspection dates in their local timezone
 */

// Map US states to their primary timezone
export const STATE_TIMEZONES: Record<string, string> = {
  // Eastern Time (EST/EDT)
  'CT': 'America/New_York',
  'DE': 'America/New_York',
  'FL': 'America/New_York',
  'GA': 'America/New_York',
  'MA': 'America/New_York',
  'MD': 'America/New_York',
  'ME': 'America/New_York',
  'MI': 'America/New_York',
  'NC': 'America/New_York',
  'NH': 'America/New_York',
  'NJ': 'America/New_York',
  'NY': 'America/New_York',
  'OH': 'America/New_York',
  'PA': 'America/New_York',
  'RI': 'America/New_York',
  'SC': 'America/New_York',
  'VA': 'America/New_York',
  'VT': 'America/New_York',
  'WV': 'America/New_York',
  'DC': 'America/New_York',

  // Central Time (CST/CDT)
  'AL': 'America/Chicago',
  'AR': 'America/Chicago',
  'IA': 'America/Chicago',
  'IL': 'America/Chicago',
  'IN': 'America/Chicago', // Mostly Central, some Eastern
  'KS': 'America/Chicago',
  'KY': 'America/Chicago',
  'LA': 'America/Chicago',
  'MN': 'America/Chicago',
  'MO': 'America/Chicago',
  'MS': 'America/Chicago',
  'ND': 'America/Chicago',
  'NE': 'America/Chicago',
  'OK': 'America/Chicago',
  'SD': 'America/Chicago',
  'TN': 'America/Chicago',
  'TX': 'America/Chicago',
  'WI': 'America/Chicago',

  // Mountain Time (MST/MDT)
  'AZ': 'America/Phoenix', // No DST
  'CO': 'America/Denver',
  'ID': 'America/Denver',
  'MT': 'America/Denver',
  'NM': 'America/Denver',
  'UT': 'America/Denver',
  'WY': 'America/Denver',

  // Pacific Time (PST/PDT)
  'CA': 'America/Los_Angeles',
  'NV': 'America/Los_Angeles',
  'OR': 'America/Los_Angeles',
  'WA': 'America/Los_Angeles',

  // Alaska (AKST/AKDT)
  'AK': 'America/Anchorage',

  // Hawaii (HST)
  'HI': 'America/Honolulu',
};

// Get timezone abbreviation for a state
export function getTimezoneAbbr(state: string, date?: Date): string {
  const timezone = STATE_TIMEZONES[state?.toUpperCase()];
  if (!timezone) return 'UTC';

  const testDate = date || new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short'
  });

  const parts = formatter.formatToParts(testDate);
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value || 'UTC';
}

// Format date in inspection's local timezone
export function formatInspectionDate(
  dateString: string | null | undefined,
  state: string,
  options: {
    includeTime?: boolean;
    includeTimezone?: boolean;
  } = {}
): string {
  if (!dateString) return '-';

  const { includeTime = false, includeTimezone = true } = options;

  try {
    // Parse as UTC date
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const timezone = STATE_TIMEZONES[state?.toUpperCase()] || 'America/New_York';

    // Format in local timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    });

    let formatted = formatter.format(date);

    // Add timezone abbreviation
    if (includeTimezone) {
      const tzAbbr = getTimezoneAbbr(state, date);
      formatted += ` ${tzAbbr}`;
    }

    return formatted;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}

// Format date for display in tables (short format with timezone)
export function formatInspectionDateShort(
  dateString: string | null | undefined,
  state: string
): string {
  if (!dateString) return '-';

  try {
    // Extract just the date part (YYYY-MM-DD) to avoid timezone conversion
    const dateOnly = dateString.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);

    // Create date in the inspection's local timezone (not UTC)
    const timezone = STATE_TIMEZONES[state?.toUpperCase()] || 'America/Chicago';

    // Create a date string that will be interpreted in the target timezone
    // Use noon to avoid any DST edge cases
    const localDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00`;

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Parse in target timezone
    const date = new Date(localDateStr);
    const formatted = formatter.format(date);
    const tzAbbr = getTimezoneAbbr(state, date);

    return `${formatted} ${tzAbbr}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}
